import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ForumDiscussion } from '../types/debate';
import { useAuth } from '../hooks/useAuth';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ChatBubbleLeftIcon,
  EyeIcon,
  ChevronLeftIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import {
  ArrowUpIcon as ArrowUpSolidIcon,
  ArrowDownIcon as ArrowDownSolidIcon
} from '@heroicons/react/24/solid';
import { doc, getDoc, updateDoc, increment, collection, addDoc, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

interface ThreadedResponse {
  id: string;
  discussionId: string;
  parentId?: string;
  authorId: string;
  authorName: string;
  content: string;
  timestamp: string;
  depth: number;
}

export const ForumThreadView: React.FC = () => {
  const { debateId, discussionId } = useParams<{ debateId: string; discussionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [discussion, setDiscussion] = useState<ForumDiscussion | null>(null);
  const [responses, setResponses] = useState<ThreadedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userVote, setUserVote] = useState<'upvote' | 'downvote' | null>(null);

  useEffect(() => {
    if (discussionId) {
      fetchDiscussion();
      fetchResponses();
    }
  }, [discussionId]);

  const fetchDiscussion = async () => {
    if (!discussionId) return;

    try {
      setLoading(true);
      setError('');

      const discussionRef = doc(db, 'forum_discussions', discussionId);
      const discussionDoc = await getDoc(discussionRef);

      if (!discussionDoc.exists()) {
        setError('Discussion not found');
        return;
      }

      const data = discussionDoc.data();
      const discussionData: ForumDiscussion = {
        id: discussionDoc.id,
        ...data,
        timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp,
        lastActivityAt: data.lastActivityAt?.toDate?.()?.toISOString() || data.lastActivityAt
      } as ForumDiscussion;

      setDiscussion(discussionData);

      // Check user's vote
      if (user && data.votes && data.votes[user.uid]) {
        setUserVote(data.votes[user.uid]);
      }
    } catch (err) {
      console.error('Error fetching discussion:', err);
      setError('Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    if (!discussionId) return;

    try {
      const responsesRef = collection(db, 'threaded_responses');
      const q = query(
        responsesRef,
        where('discussionId', '==', discussionId),
        orderBy('timestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const fetchedResponses: ThreadedResponse[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        fetchedResponses.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp
        } as ThreadedResponse);
      });

      setResponses(fetchedResponses);
    } catch (err) {
      console.error('Error fetching responses:', err);
    }
  };

  const handleVote = async (voteType: 'upvote' | 'downvote') => {
    if (!user || !discussion) {
      setError('You must be logged in to vote');
      return;
    }

    try {
      const discussionRef = doc(db, 'forum_discussions', discussion.id);
      const newVote = userVote === voteType ? null : voteType;

      let upvoteChange = 0;
      let downvoteChange = 0;

      // Calculate changes
      if (userVote === 'upvote') upvoteChange = -1;
      if (userVote === 'downvote') downvoteChange = -1;
      if (newVote === 'upvote') upvoteChange += 1;
      if (newVote === 'downvote') downvoteChange += 1;

      // Update Firestore
      const updates: any = {};
      if (upvoteChange !== 0) updates.upvotes = increment(upvoteChange);
      if (downvoteChange !== 0) updates.downvotes = increment(downvoteChange);

      if (newVote) {
        updates[`votes.${user.uid}`] = newVote;
      } else {
        updates[`votes.${user.uid}`] = null;
      }

      await updateDoc(discussionRef, updates);

      // Update local state
      setUserVote(newVote);
      setDiscussion({
        ...discussion,
        upvotes: discussion.upvotes + upvoteChange,
        downvotes: discussion.downvotes + downvoteChange
      });
    } catch (err) {
      console.error('Error voting:', err);
      setError('Failed to register vote');
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !discussion) {
      setError('You must be logged in to reply');
      return;
    }

    if (!replyContent.trim()) {
      setError('Reply content cannot be empty');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const responsesRef = collection(db, 'threaded_responses');
      await addDoc(responsesRef, {
        discussionId: discussion.id,
        parentId: discussion.id,
        authorId: user.uid,
        authorName: user.email?.split('@')[0] || 'Anonymous',
        content: replyContent.trim(),
        timestamp: serverTimestamp(),
        depth: 0
      });

      // Increment reply count
      const discussionRef = doc(db, 'forum_discussions', discussion.id);
      await updateDoc(discussionRef, {
        replyCount: increment(1),
        lastActivityAt: serverTimestamp()
      });

      setReplyContent('');
      await fetchResponses();
      await fetchDiscussion();
    } catch (err) {
      console.error('Error submitting reply:', err);
      setError('Failed to submit reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-400">Loading discussion...</div>
      </div>
    );
  }

  if (error && !discussion) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button
          onClick={() => navigate(`/debate/${debateId}`)}
          className="text-blue-400 hover:text-blue-300"
        >
          Back to Debate
        </button>
      </div>
    );
  }

  if (!discussion) {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(`/debate/${debateId}`)}
        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6"
      >
        <ChevronLeftIcon className="w-4 h-4" />
        Back to Debate
      </button>

      {/* Discussion Header */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
        {/* Category Badge */}
        <div className="mb-3">
          <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
            {discussion.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-white mb-4">{discussion.title}</h1>

        {/* Author and Metadata */}
        <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
          <span>by {discussion.authorName}</span>
          <span>•</span>
          <span>{formatTimestamp(discussion.timestamp)}</span>
          <span className="flex items-center gap-1">
            <EyeIcon className="w-4 h-4" />
            {discussion.viewCount} views
          </span>
        </div>

        {/* Content */}
        <div className="text-gray-200 whitespace-pre-wrap mb-6">
          {discussion.content}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4">
          {/* Voting */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleVote('upvote')}
              className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                userVote === 'upvote'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
              disabled={!user}
            >
              {userVote === 'upvote' ? <ArrowUpSolidIcon className="w-4 h-4" /> : <ArrowUpIcon className="w-4 h-4" />}
              {discussion.upvotes}
            </button>
            <button
              onClick={() => handleVote('downvote')}
              className={`flex items-center gap-1 px-3 py-1 rounded transition-colors ${
                userVote === 'downvote'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
              disabled={!user}
            >
              {userVote === 'downvote' ? <ArrowDownSolidIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />}
              {discussion.downvotes}
            </button>
          </div>

          {/* Reply Count */}
          <div className="flex items-center gap-1 text-gray-400">
            <ChatBubbleLeftIcon className="w-4 h-4" />
            <span>{discussion.replyCount} {discussion.replyCount === 1 ? 'reply' : 'replies'}</span>
          </div>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="flex items-center gap-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors ml-auto"
          >
            <ShareIcon className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-700 rounded text-red-200">
          {error}
        </div>
      )}

      {/* Reply Form */}
      {user ? (
        <form onSubmit={handleSubmitReply} className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write a reply..."
            rows={4}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical mb-3"
            disabled={isSubmitting}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !replyContent.trim()}
            >
              {isSubmitting ? 'Posting...' : 'Post Reply'}
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6 text-center text-gray-400">
          Please log in to reply to this discussion
        </div>
      )}

      {/* Replies */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white mb-4">
          Replies ({responses.length})
        </h2>
        {responses.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No replies yet. Be the first to respond!
          </div>
        ) : (
          responses.map((response) => (
            <div
              key={response.id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <span className="font-medium text-gray-300">{response.authorName}</span>
                <span>•</span>
                <span>{formatTimestamp(response.timestamp)}</span>
              </div>
              <div className="text-gray-200 whitespace-pre-wrap">
                {response.content}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
